import { SignInRequestSchema } from "@pokifolio/contracts";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { type SubmitEvent, useState } from "react";

import { useApi } from "../../app/api-context";
import { ErrorAlert } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import { Field } from "../../components/ui/field";
import { Input } from "../../components/ui/input";
import { type FieldErrors, validate } from "../../lib/forms";
import { AuthLayout } from "./auth-layout";
import { useResetSession } from "./session";

export function LoginPage() {
  const api = useApi();
  const navigate = useNavigate();
  const resetSession = useResetSession();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<FieldErrors>({});

  const signIn = useMutation({
    mutationFn: api.signIn,
    onSuccess: async () => {
      await resetSession();
      await navigate({ to: "/" });
    },
  });

  function onSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    const result = validate(SignInRequestSchema, { email, password });
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }
    setErrors({});
    signIn.mutate(result.data);
  }

  return (
    <AuthLayout title="Sign in" description="Use the account your administrator created for you.">
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        <Field id="email" label="Email" error={errors.email}>
          <Input
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field id="password" label="Password" error={errors.password}>
          <Input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        {signIn.isError && <ErrorAlert error={signIn.error} />}
        <Button type="submit" disabled={signIn.isPending}>
          {signIn.isPending ? "Signing in…" : "Sign in"}
        </Button>
      </form>
    </AuthLayout>
  );
}
